import { useCallback, useRef, useState } from 'react';

import {
  Drawer,
  PrimaryButton,
  TertiaryButton,
} from '@changegps/horizon-component-library';
import { Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useUserInfo } from '@/features/users';

import { subscriptionsQueries } from '../../data';
import { useToastStore } from '../../store';
import { BillingDetails } from '../../types';
import {
  BillingDetailsForm,
  BillingDetailsFormRef,
  CardDetailsForm,
  CardDetailsFormRef,
} from '../form';

interface BillingDrawerProps {
  type: 'card' | 'billing';
  isVisible: boolean;
  toggleIsVisible: () => void;
  billingDetails?: BillingDetails | null;
}

export const BillingDrawer = ({
  type,
  isVisible,
  toggleIsVisible,
  billingDetails,
}: BillingDrawerProps) => {
  const updateCardRef = useRef<CardDetailsFormRef>(null);
  const updateBillingRef = useRef<BillingDetailsFormRef>(null);

  const queryClient = useQueryClient();

  const { company_id } = useUserInfo();
  const { showToast } = useToastStore();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSuccess = useCallback(
    async (msg: string) => {
      if (type === 'billing') {
        await queryClient.invalidateQueries({
          queryKey: [...subscriptionsQueries.getBillingInfo, company_id],
        });
      }
      setIsSubmitting(false);
      showToast(msg, 'success');
      toggleIsVisible();
    },
    [showToast, toggleIsVisible],
  );

  const handleError = useCallback(
    (msg: string) => {
      setIsSubmitting(false);
      showToast(msg, 'error');
    },
    [showToast],
  );

  const handleSubmissionStart = useCallback(() => {
    setIsSubmitting(true);
  }, []);

  return (
    <>
      <Drawer
        title={`Change ${type} details`}
        content={
          <>
            {type === 'card' ? (
              <CardDetailsForm
                ref={updateCardRef}
                companyID={company_id}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            ) : (
              <BillingDetailsForm
                ref={updateBillingRef}
                billingDetails={billingDetails}
                companyID={company_id}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            )}
          </>
        }
        onClose={() => {
          if (!isSubmitting) toggleIsVisible();
        }}
        open={isVisible}
        actions={
          <Stack direction="row" justifyContent="space-between" width="100%">
            <TertiaryButton
              onClick={() => toggleIsVisible()}
              disabled={isSubmitting}
            >
              Cancel
            </TertiaryButton>
            <PrimaryButton
              onClick={() => {
                handleSubmissionStart();
                if (type === 'card') {
                  updateCardRef.current?.submitForm();
                } else {
                  updateBillingRef.current?.submitForm();
                }
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm'}
            </PrimaryButton>
          </Stack>
        }
      />
    </>
  );
};
